import type { ReactElement } from 'react'

import { GPSRInformation } from '../epages.base/GPSRInformation'
import { PriceStructuredData, getProductMicrodata } from '../epages.base/ProductPrice'
import { ProductDetailProvider } from '../../productDetails/ProductDetailProvider'
import BulkPrices from '../epages.base/BulkPrices'
import Buybox from '../../productDetails/Buybox'
import BuyboxThemeComponent from './Buybox'
import Crossselling from '../epages.base/Crossselling'
import CustomAttributes from '../epages.base/CustomAttributes'
import EnergyLabel from '../../templateComponents/EnergyLabel'
import GaranLabel from '../../templateComponents/GaranLabel'
import Link from '../../templateComponents/Link'
import Percentage from '../../productDetails/Percentage'
import Price from '../../productDetails/Price'
import PriceReference from '../../productDetails/PriceReference'
import ProductDescription from '../epages.base/ProductDescription'
import ProductImage from '../epages.base/ProductImage'
import ProductImageGallery from '../epages.base/ProductImageGallery'

export default function Product({ product, shop, t }: ProductThemeComponentProps): ReactElement {
  return (
    <div className="product" {...getProductMicrodata(product)}>
      {product.sku && <meta itemProp="sku" content={product.sku} />}
      {product.gtin && <meta itemProp={`gtin${product.gtin.length}`} content={product.gtin} />}
      {product.manufacturer && <meta itemProp="brand" content={product.manufacturer} />}
      {product.productVariationSelection?.map((variation) => (
        <div key={variation.value} itemProp="additionalProperty" itemScope itemType="https://schema.org/PropertyValue">
          <meta itemProp="name" content={variation.name} />
          <meta itemProp="value" content={variation.displayValue} />
        </div>
      ))}
      <section className="section">
        <div className="wrapper">
          <div className="content">
            {product.slideshow ? (
              <div className="product-image-gallery-wrapper">
                <ProductImageGallery product={product} thumbnailListIsVertical={true} productLabels={product.labels} />
              </div>
            ) : (
              <ProductImage image={product.image} alt={product.name} productLabels={product.labels} />
            )}

            <ProductDetailProvider productId={product.productId} minQuantity={product.orderUnitInfo.minOrder}>
              <div className="product-info">
                <div className="product-info-details">
                  <div className="product-info-details-basic">
                    <h1 className="product-info-details-basic-title" itemProp="name">
                      {product.name}
                    </h1>
                    <div id="product-info-top-content" />
                  </div>
                  <PriceStructuredData product={product} />
                  <div className="product-info-details-price">
                    {product.strikePrice && !product.hasVariations && product.price && !product.isUniquePrice && (
                      <div className="product-info-details-price-old">
                        {product.isStrikePriceRRP && (
                          <span className="product-info-details-price-manufacturer">
                            {t('components.productComponent.priceManufacturer')}
                          </span>
                        )}
                        <div>
                          <span className="visually-hidden">
                            {t('components.productComponent.strikePrice.accessibilityLabel', {
                              price: product.strikePrice.formatted,
                            })}
                          </span>
                          <span aria-hidden="true">{product.strikePrice.formatted}</span>
                        </div>
                        <Percentage
                          strikePriceAmount={product.strikePrice.amount}
                          priceAmount={product.price.amount}
                          bulkPrices={product.bulkPrices}
                          className="product-info-details-price-percentage"
                        />
                      </div>
                    )}
                    {product.lowestPrice && (
                      <h3 className="product-info-details-price-single rows">
                        {t('components.productComponent.variationLowestPrice', {
                          amount: product.lowestPrice.formatted,
                        })}
                      </h3>
                    )}
                    {product.price && !product.lowestPrice && (
                      <Price
                        className={
                          product.strikePrice && !product.isUniquePrice
                            ? 'product-info-details-price-new'
                            : 'product-info-details-price-single'
                        }
                        product={product}
                      />
                    )}
                    {product.basePrice && (
                      <PriceReference
                        lowestRefPriceFormatted={product.lowestRefPriceFormatted}
                        basePriceFormatted={product.basePrice.formatted}
                        bulkPrices={product.bulkPrices}
                        className="product-info-details-basic-reference"
                      />
                    )}

                    {product.isUniquePrice && (
                      <div className="product-info-box-your-price-note">
                        {t('components.productComponent.customerGroupPrice')}
                      </div>
                    )}

                    <div className="product-info-details-vat-note">
                      {product.vatNote && t(product.vatNote, { nsSeparator: '__' })}
                      {product.vatNote && product.isShippingLinkVisible && ', '}
                      {product.isShippingLinkVisible && (
                        <Link className="product-info-details-shipping-link" to={shop.legalPageUrls.shipping}>
                          {' '}
                          {t('components.productComponent.plusShipping')}
                        </Link>
                      )}
                      {product.deposit && (
                        <>
                          {product.vatNote || product.isShippingLinkVisible ? ', ' : null}

                          {t('components.productComponent.deposit', {
                            amount: product.deposit.formatted,
                          })}
                        </>
                      )}
                    </div>
                    {shop.beyond && product.weight && (
                      <div className="product-info-details-product-weight">
                        {t('components.productComponent.productWeight', { weight: product.weight.formatted })}
                      </div>
                    )}
                  </div>
                  <div id="ep-app-area-product-view-price"></div>
                </div>

                {product.isProductNumberVisible && (
                  <p className="product-info-sku">
                    {t('components.productComponent.productNumber', {
                      sku: product.sku,
                    })}
                  </p>
                )}
                {(product.energyLabel || product.productDataSheet || product.garanLabel) && (
                  <div className="product-info-energy-label">
                    {product.energyLabel && (
                      <EnergyLabel
                        t={t}
                        energyLabel={product.energyLabel}
                        energyLabelSourceFile={product.energyLabelSourceFile}
                      />
                    )}
                    {product.productDataSheet && (
                      <a
                        href={product.productDataSheet.dataUri}
                        target="_blank"
                        rel="noreferrer"
                        className="product-info-energy-label-product-data-sheet"
                      >
                        {product.productDataSheet.label}
                      </a>
                    )}

                    {product.garanLabel && (
                      <div className="product-info-energy-label-garan-label">
                        <GaranLabel garanLabel={product.garanLabel} nestedGaranLabel={product.nestedGaranLabel} />
                      </div>
                    )}
                  </div>
                )}
                <Buybox product={product}>
                  <BuyboxThemeComponent t={t} product={product} shop={shop} />
                </Buybox>
              </div>
              <div className="product-addition-wrapper">
                <hr />
                <ProductDescription
                  description={product.description}
                  additionalDescriptions={product.additionalDescriptions}
                  t={t}
                />
                <CustomAttributes customAttributes={product.customAttributes} t={t} />
                <BulkPrices product={product} t={t} />
                <GPSRInformation gpsrInfo={product.gpsrInfo} t={t} />
                <div id="product-info-bottom-content" />
              </div>
              <div id="product-footer-content" />
            </ProductDetailProvider>
          </div>
          {product.hasCrossSelling && <Crossselling product={product} />}
        </div>
      </section>
    </div>
  )
}
